<script>
export default {
    name: "StaffVacationTrackerTable",
    props: {
        staffId: {
            type: Number,
            required: true,
        },
        leaveType: {
            type: String,
            required: false,
            default: "vacation",
        },
    },
    data() {
        return {
            isLoading: true,
        };
    },
    computed: {
        vacationListSortByYear() {
            const yearlyList = this.groupVacationByYear(this.vacationList);
            return this.calculateYearlyStats(yearlyList);
        },
        sortedYears() {
            return Object.keys(this.vacationListSortByYear).sort((a, b) => Number(b) - Number(a));
        },
        currentYear() {
            return new Date().getFullYear();
        },
        leaveText() {
            return this.leaveType === "vacation" ? "Vacation" : "Sick Leave";
        },
        vacationList() {
            if (this.leaveType === "vacation") {
                return this.$store.getters["vacation/vacationList"];
            } else {
                return this.$store.getters["vacation/sickLeaveList"];
            }
        },
    },
    mounted() {
        this.getVacationList(this.leaveType);
    },
    methods: {
        showTotalDays(vacation, type) {
            return type === "vacation" ? vacation.totalVacationDays : vacation.totalSickLeaveDays;
        },
        showApprovedDays(vacation, type) {
            return type === "vacation" ? vacation.approvedVacationsDays : vacation.approvedSickLeaveDays;
        },
        usedPercent(stats) {
            const total = this.leaveType === "vacation" ? stats.totalVacationDays : stats.totalSickLeaveDays;
            const used = this.leaveType === "vacation" ? stats.approvedVacationsDays : stats.approvedSickLeaveDays;
            return total > 0 ? Math.min((used / total) * 100, 100) : 0;
        },
        progressBarClass(percent) {
            if (percent >= 90) return "bg-danger";
            if (percent >= 70) return "bg-warning";
            return "bg-success";
        },
        async getVacationList(leaveType) {
            this.isLoading = true;
            try {
                await this.$store.dispatch("vacation/getStaffVacations", { staffId: this.staffId, leaveType: leaveType });
            } finally {
                this.isLoading = false;
            }
        },
        groupVacationByYear(vacationList) {
            const yearlyVacations = {};
            for (const vacation of vacationList) {
                const startYear = vacation.start_year;
                const endYear = String(vacation.end_year);
                const startYearDays = parseFloat(vacation.days_in_start_year);
                const endYearDays = parseFloat(vacation.days_in_end_year);
                if (!yearlyVacations[startYear]) yearlyVacations[startYear] = [];
                yearlyVacations[startYear].push({ ...vacation, _yearContext: startYear, _daysInYear: startYearDays });
                if (startYear !== endYear && endYearDays > 0) {
                    if (!yearlyVacations[endYear]) yearlyVacations[endYear] = [];
                    yearlyVacations[endYear].push({ ...vacation, _yearContext: endYear, _daysInYear: endYearDays });
                }
            }
            return yearlyVacations;
        },
        calculateYearlyStats(vacations) {
            const yearlyStats = {};
            for (const year in vacations) {
                const vacationList = vacations[year];
                const totalVacationDays = vacationList[0].total_vacation_days;
                const totalSickLeaveDays = vacationList[0].total_sick_leave_days;
                let approvedVacationsDays = 0;
                let approvedSickLeaveDays = 0;
                let encash = vacationList[0].staff_encash_days;
                for (const vacation of vacationList) {
                    const daysToUse = vacation._daysInYear !== undefined ? vacation._daysInYear : vacation.total_days;
                    if (vacation.is_approved === 1) {
                        if (vacation.sick_leave === 1) {
                            approvedSickLeaveDays += daysToUse;
                        } else {
                            approvedVacationsDays += daysToUse;
                        }
                    }
                }
                const balance = vacationList[0].sick_leave === 1
                    ? totalSickLeaveDays - approvedSickLeaveDays
                    : totalVacationDays - approvedVacationsDays;
                yearlyStats[year] = {
                    totalVacationDays,
                    totalSickLeaveDays,
                    approvedVacationsDays,
                    approvedSickLeaveDays,
                    balance,
                    encash,
                    finalBalance: balance - encash,
                    vacations: vacationList,
                };
            }
            return yearlyStats;
        },
    },
};
</script>

<template>
  <div>
    <div v-if="isLoading" class="block block-rounded my-3">
      <div class="block-header block-header-default">
        <h3 class="block-title">
          {{ leaveText }} Tracker
        </h3>
      </div>
      <div class="block-content">
        <div class="text-center pt-3 pb-3">
          <span class="spinner-border text-primary" role="status" aria-hidden="true"></span>
          <span class="ms-2">Loading {{ leaveText.toLowerCase() }} requests...</span>
        </div>
      </div>
    </div>
    
    <div
      v-else-if="vacationList.length"
      class="block block-rounded my-3"
    >
    <div class="block-header block-header-default">
      <h3 class="block-title">
        {{ leaveText }} Tracker for {{ vacationList[0]?.staff?.user?.full_name ?? "" }}
      </h3>
    </div>
    <div class="block-content">
      <div class="table-responsive">
        <table class="table table-sm table-striped">
          <thead>
            <tr>
              <th>Year</th>
              <th>Total</th>
              <th>Approved</th>
              <th>Balance</th>
              <th v-if="leaveType === 'vacation'">Encash</th>
              <th>Final</th>
              <th>Usage</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="year in sortedYears" :key="year">
              <td class="fw-semibold">{{ year }}</td>
              <td>{{ showTotalDays(vacationListSortByYear[year], leaveType) }}</td>
              <td>{{ showApprovedDays(vacationListSortByYear[year], leaveType) }}</td>
              <td>{{ vacationListSortByYear[year].balance }}</td>
              <td v-if="leaveType === 'vacation'">{{ vacationListSortByYear[year].encash }}</td>
              <td>{{ vacationListSortByYear[year].finalBalance }}</td>
              <td style="min-width: 120px">
                <div
                  class="progress"
                  role="progressbar"
                  :aria-valuenow="usedPercent(vacationListSortByYear[year])"
                  aria-valuemin="0"
                  aria-valuemax="100"
                  aria-label="Leave used for year"
                  style="height: 6px"
                >
                  <div
                    class="progress-bar"
                    :class="progressBarClass(usedPercent(vacationListSortByYear[year]))"
                    :style="{ width: usedPercent(vacationListSortByYear[year]) + '%' }"
                  />
                </div>
                <small class="text-muted">{{ usedPercent(vacationListSortByYear[year]).toFixed(0) }}%</small>
              </td>
              <td>
                <a
                  v-if="leaveType === 'vacation'"
                  :href="`/admin/vacation/list/${year}?staff_id=${staffId}`"
                  class="btn btn-sm btn-outline-primary"
                >
                  View
                </a>
                <a
                  v-else
                  :href="`/admin/vacation/list/${year}?staff_id=${staffId}&leave_type=sick`"
                  class="btn btn-sm btn-outline-primary"
                >
                  View
                </a>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    </div>
    
    <div
      v-else
      class="block block-rounded my-3"
    >
      <div class="block-header block-header-default">
        <h3 class="block-title">
          {{ leaveText }} Tracker
        </h3>
      </div>
      <div class="block-content">
        <p class="text-muted text-center pt-3 pb-3">No {{ leaveText.toLowerCase() }} requests found for this staff.</p>
      </div>
    </div>
  </div>
</template>
